import axios from 'axios';
import { isObject } from 'lodash';
import moment from 'moment';
import 'moment/locale/fr';
import 'moment/locale/en-gb';
import { CustomFieldsResults } from './types';

export const downloadFile = (url: string, fileName: string) => {
  axios({
    //Found on stackoverflow -> https://stackoverflow.com/questions/41938718/how-to-download-files-using-axios
    url: url,
    method: 'GET',
    responseType: 'blob'
  }).then((response) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName); //or any other extension
    document.body.appendChild(link);
    link.click();
  });
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const removeAccents = (str: string) => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

export const getFilterString = (filterValues: { [fieldName: string]: any }) => {
  let filterString = '';
  Object.keys(filterValues).forEach((filterName) => {
    if (typeof filterValues[filterName] === 'undefined' || filterValues[filterName] === null) {
      return;
    }
    if (filterName === 'campaignId') {
      filterString += `&campaign=${filterValues[filterName]}`;
    }
    if (filterName.includes('created_at')) {
      filterString += `&${filterName}=${moment(filterValues[filterName]).format('YYYY-MM-DD')}`;
    } else if (filterValues[filterName] instanceof Array) {
      const filterNameSuffix =
        filterValues[filterName].length > 1 &&
        filterName.substring(filterName.length - 4) !== '__in'
          ? '__in'
          : '';
      filterString += `&${filterName}${filterNameSuffix}=${filterValues[filterName].map((obj: { value: string | number }) => obj.value).join(',')}`;
    } else if (isObject(filterValues[filterName]) && 'value' in filterValues[filterName]) {
      filterString += `&${filterName}=${filterValues[filterName].value}`;
    } else {
      filterString += `&${filterName}=${filterValues[filterName]}`;
    }
  });
  return filterString;
};

export const removeFalsyValues = (obj: Record<string, any>): Record<string, any> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => {
      // Keep entries where the value is not falsy, empty string, or empty array
      return Boolean(value) && !(Array.isArray(value) && value.length === 0) && value !== '';
    })
  );
};

export function removeDuplicates(arr: any[]) {
  return arr.filter((item: any, index: any) => arr.indexOf(item) === index);
}

export const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

export const truncateWithEllipsis = (input: string, length?: number) => {
  const defaultLength = 15;
  return input.length > (length || defaultLength)
    ? `${input.substring(0, length || defaultLength)}...`
    : input;
};

export const getValueByPath = <T, K extends string>(el: T, path: K): unknown => {
  return path
    .split('.')
    .reduce<unknown>(
      (acc, key) =>
        acc !== undefined && acc !== null && typeof acc === 'object'
          ? (acc as Record<string, unknown>)[key]
          : undefined,
      el
    );
};

export const formatDate = (value: string | Date | number, locale = 'fr', seconds = false) => {
  const PATTERNS: Record<string, string> = {
    fr: seconds ? 'DD/MM/YYYY à HH:mm:ss' : 'DD/MM/YYYY à HH:mm',
    'en-gb': seconds ? 'DD/MM/YYYY HH:mm:ss' : 'DD/MM/YYYY HH:mm',
    en: seconds ? 'MM/DD/YYYY hh:mm:ss A' : 'MM/DD/YYYY hh:mm A'
  };
  const pattern = PATTERNS[locale] ?? PATTERNS.fr;
  moment.locale(locale === 'en' ? 'en-gb' : locale);
  if (typeof value === 'number') {
    return moment.unix(value).format(pattern);
  }

  return moment(value).format(pattern);
};

export const lowercaseFirst = (str: string) => str.charAt(0).toLowerCase() + str.slice(1);

export const formatToSelectOptions = (
  data: any[] | undefined,
  customFieldsResults?: CustomFieldsResults
) => {
  if (!Array.isArray(data)) return undefined;
  return data.map((item: any) => ({
    value: item[customFieldsResults?.value || 'id'],
    label: item[customFieldsResults?.label || 'label']
  }));
};
